import React, { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import {
  getOrganizationsEnterprisesSelector,
  getCatalogProductTypesSelector,
} from "../../../../reducers/Organizations/organizations"
import { ACTIVE_KFC_BRANDS, COLORS } from "../../../../utils"
import {
  getAvailableProductTypes,
  previewCatalogGeneration,
  generateEnterpriseCatalog,
} from "../../../../actions/Organizations/Enterprise"
import CatalogSourceRow from "./CatalogSourceRow"

const sourceKey = (sourceType, source) => `${sourceType}__${source}`

const makeRow = () => ({
  key: `row-${Date.now()}-${Math.random()}`,
  sourceKey: "",
  productTypes: [],
  availableProductTypes: [],
  loadingTypes: false,
})

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 640,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "8px",
    },
  },
  title: {
    px: 5,
    pt: 3,
    pb: 1,
    fontSize: 18,
    fontWeight: 500,
    pr: 8,
    marginBottom: "20px !important",
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    px: 5,
    pt: 1,
    pb: 2,
  },
  rowsList: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  addSourceBtn: {
    alignSelf: "flex-end",
    mt: 1,
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    py: "4px",
  },
  confirmBox: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  infoAlert: {
    backgroundColor: "#E3F2FD",
    ".MuiAlert-icon": {
      color: "#0288D1",
    },
    ".MuiAlert-message": {
      fontSize: 14,
      color: COLORS.BLACK800
    }
  },
  confirmSourceCard: {
    border: "1px solid",
    borderColor: "divider",
    borderRadius: "8px",
    p: 2,
  },
  confirmSourceHeader: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    mb: 1,
  },
  confirmSourceName: {
    fontSize: 15,
    fontWeight: 500,
  },
  confirmMergeTag: {
    fontSize: 12,
    color: COLORS.GREEN_SUCCESS_800,
  },
  confirmCount: {
    fontSize: 15,
    fontWeight: 500,
  },
  serverError: {
    px: 5,
    pb: 1,
    fontSize: 14,
    color: "#B71C1C",
  },
  actions: {
    px: 5,
    pt: 3,
    pb: 4,
    justifyContent: "space-between",
    gap: 1,
  },
}

const getSourceOptions = (enterpriseId, allEnterprises) => [
  ...ACTIVE_KFC_BRANDS.map(brand => ({ sourceType: "BRAND", source: brand.name, label: brand.label, image: brand.image })),
  ...allEnterprises
    .filter(enterprise => enterprise.objectId !== enterpriseId)
    .map(enterprise => ({ sourceType: "ENTERPRISE", source: enterprise.objectId, label: enterprise.name, logo: enterprise.logo })),
]

const CatalogSourceModal = ({ open, onClose, enterpriseId, existingSources }) => {
  const dispatch = useDispatch()
  const allEnterprises = useSelector(getOrganizationsEnterprisesSelector)
  const catalogProductTypes = useSelector(getCatalogProductTypesSelector)

  const [step, setStep] = useState("source")
  const [rows, setRows] = useState([makeRow()])
  const [submitted, setSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [serverError, setServerError] = useState(null)
  const [previewCount, setPreviewCount] = useState(null)

  const sourceOptions = getSourceOptions(enterpriseId, allEnterprises)

  const existingSourceKeys = (existingSources || []).map(s => sourceKey(s.sourceType, s.source))

  const getExistingProductTypes = (rowSourceKey) => {
    const existingSource = (existingSources || []).find(s => sourceKey(s.sourceType, s.source) === rowSourceKey)
    return existingSource ? existingSource.productTypes : []
  }

  const isMergeRow = (row) => existingSourceKeys.includes(row.sourceKey)

  const getProductTypeLabel = (kfcName) => {
    const match = catalogProductTypes.find(pt => pt.kfcName === kfcName)
    return match ? match.label : kfcName
  }

  const updateRow = (key, patch) => {
    setRows(prev => prev.map(row => row.key === key ? { ...row, ...patch } : row))
  }

  const addRow = () => setRows(prev => [...prev, makeRow()])

  const removeRow = (key) => setRows(prev => prev.filter(row => row.key !== key))

  const resetState = () => {
    setStep("source")
    setRows([makeRow()])
    setSubmitted(false)
    setServerError(null)
    setPreviewCount(null)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSourceChange = async (key, value) => {
    const row = rows.find(r => r.key === key)
    const previousExistingTypes = getExistingProductTypes(row.sourceKey)
    const newExistingTypes = getExistingProductTypes(value)
    const carriedOverTypes = row.productTypes.filter(pt => !previousExistingTypes.includes(pt))
    const nextProductTypes = [...new Set([...newExistingTypes, ...carriedOverTypes])]

    updateRow(key, { sourceKey: value, productTypes: nextProductTypes, availableProductTypes: [], loadingTypes: true })

    const opt = sourceOptions.find(o => sourceKey(o.sourceType, o.source) === value)
    if (!opt) {
      updateRow(key, { loadingTypes: false })
      return
    }

    try {
      const types = await dispatch(getAvailableProductTypes(opt.sourceType, opt.source))
      updateRow(key, { availableProductTypes: types || [] })
    } finally {
      updateRow(key, { loadingTypes: false })
    }
  }

  const isDuplicateSource = (row) =>
    Boolean(row.sourceKey) && rows.some(other => other.key !== row.key && other.sourceKey === row.sourceKey)

  const isRowIncomplete = (row) => {
    if (isMergeRow(row)) {
      const lockedProductTypes = getExistingProductTypes(row.sourceKey)
      return row.productTypes.filter(pt => !lockedProductTypes.includes(pt)).length === 0
    }
    return row.productTypes.length === 0
  }

  const hasBlockingErrors = rows.some(row =>
    !row.sourceKey || isDuplicateSource(row) || isRowIncomplete(row)
  )

  const buildSourcesPayload = () =>
    rows.map(row => {
      const opt = sourceOptions.find(o => sourceKey(o.sourceType, o.source) === row.sourceKey)
      return { sourceType: opt.sourceType, source: opt.source, productTypes: row.productTypes }
    })

  const handleNext = async () => {
    setSubmitted(true)
    if (hasBlockingErrors) return

    setIsSubmitting(true)
    setServerError(null)
    try {
      const preview = await dispatch(previewCatalogGeneration(enterpriseId, buildSourcesPayload()))
      setPreviewCount(preview.impactedRecipesCount)
      setStep("confirm")
    } catch (e) {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleGenerate = async () => {
    setIsSubmitting(true)
    const succeeded = await dispatch(generateEnterpriseCatalog(enterpriseId, buildSourcesPayload()))
    setIsSubmitting(false)

    if (succeeded) handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} sx={sx.dialog}>
      <DialogTitle sx={sx.title}>
        {step === "source" ? "Créer le catalogue de recettes" : "Confirmer la génération"}
        <IconButton onClick={handleClose} disabled={isSubmitting} sx={sx.closeBtn}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={sx.content}>
        {step === "source" ? (
          <Box sx={sx.rowsList}>
            {rows.map((row) => (
              <CatalogSourceRow
                key={row.key}
                row={row}
                sourceOptions={sourceOptions}
                showDelete={rows.length > 1}
                submitted={submitted}
                isSubmitting={isSubmitting}
                isDuplicate={isDuplicateSource(row)}
                lockedProductTypes={getExistingProductTypes(row.sourceKey)}
                isMergeSource={isMergeRow(row)}
                onSourceChange={(value) => handleSourceChange(row.key, value)}
                onProductTypesChange={(values) => updateRow(row.key, { productTypes: values })}
                onRemove={() => removeRow(row.key)}
              />
            ))}

            <Button
              variant="text"
              size="small"
              startIcon={<AddIcon fontSize="small" />}
              onClick={addRow}
              disabled={isSubmitting}
              sx={sx.addSourceBtn}
            >
              Ajouter une autre source
            </Button>
          </Box>
        ) : (
          <Box sx={sx.confirmBox}>
            <Alert
              severity="info"
              icon={<InfoOutlinedIcon fontSize="small" />}
              sx={sx.infoAlert}
            >
              Seules les recettes au statut actif seront ajoutées au catalogue de recettes.
            </Alert>
            {rows.map((row) => {
              const opt = sourceOptions.find(o => sourceKey(o.sourceType, o.source) === row.sourceKey)
              return (
                <Box key={row.key} sx={sx.confirmSourceCard}>
                  <Box sx={sx.confirmSourceHeader}>
                    <Typography sx={sx.confirmSourceName}>{opt && opt.label}</Typography>
                    {isMergeRow(row) && (
                      <Typography sx={sx.confirmMergeTag}>Mise à jour d'une source existante</Typography>
                    )}
                  </Box>
                  <Box sx={sx.chips}>
                    {row.productTypes.map(pt => (
                      <Chip key={pt} label={getProductTypeLabel(pt)} size="small" />
                    ))}
                  </Box>
                </Box>
              )
            })}
            <Typography sx={sx.confirmCount}>
              {previewCount} recette{previewCount > 1 ? "s" : ""} seront ajoutées au catalogue de recettes.
            </Typography>
          </Box>
        )}
      </DialogContent>

      {serverError && (
        <Typography sx={sx.serverError}>{serverError}</Typography>
      )}

      <DialogActions sx={sx.actions}>
        {step === "confirm" && (
          <Button variant="text" onClick={() => setStep("source")} disabled={isSubmitting}>
            Retour
          </Button>
        )}
        {step === "source" && (
          <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
            Annuler
          </Button>
        )}
        <Button
          variant="contained"
          onClick={step === "source" ? handleNext : handleGenerate}
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isSubmitting
            ? "Chargement…"
            : step === "source" ? "Confirmer" : "Générer le catalogue de recettes"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CatalogSourceModal
